export const supportedTravelCountries = [
  'United Kingdom', 'United States', 'Netherlands', 'Belgium', 'Sweden',
  'Spain', 'France', 'Italy', 'Germany', 'Greece', 'Portugal',
];

const vpn = [
  ['vpn-1-hour', 'Instant 1-Hour VPN Access', '£0.20', '1 hour', 'A short, pay-as-you-go VPN pass for secure browsing when you only need protection briefly.', 'vpn-1h', '/dmb-payment_20', ['Carrier billing']],
  ['vpn-24-hour', '24-Hour VPN Pass', '£1.00', '24 hours', 'A full day of encrypted PhantomPathVPN access with no subscription or renewal.', 'vpn-24h', '/dmb-payment_100', ['Carrier billing', 'Stripe', 'PayPal']],
  ['vpn-2-week', '2-Week VPN Pass', '£2.00', '14 days', 'Two weeks of private, encrypted access at approximately 14p per day.', 'vpn-14', '/dmb-payment_200', ['Carrier billing', 'Stripe', 'PayPal']],
  ['vpn-30-day', '30-Day VPN Pass', '£3.50', '30 days', 'A month of private VPN access with the lowest daily cost and no auto-renewal.', 'vpn-30', '/dmb-payment_350', ['Carrier billing', 'Stripe', 'PayPal']],
].map(([slug, name, price, duration, description, sku, carrierRoute, paymentMethods]) => ({
  slug, name, price, amount: Number(price.replace('£', '')), duration, description, sku, carrierRoute,
  paymentMethods, category: 'vpn', categoryLabel: 'VPN Passes', accent: '#00ffc8',
  eyebrow: 'PAY-AS-YOU-GO VPN',
  features: ['AES-256 encrypted connection', 'Instant access after payment', 'No subscription or auto-renewal', 'No traditional account required', 'WireGuard configuration included'],
}));

const travelTiers = [
  ['1-week', '7 days', [['1gb', 1, 7.99], ['5gb', 5, 12.99], ['10gb', 10, 18.99], ['50gb', 50, 39.99]]],
  ['2-week', '14 days', [['5gb', 5, 23.99], ['10gb', 10, 33.99], ['50gb', 50, 69.99]]],
  ['30-day', '30 days', [['10gb', 10, 34.99], ['20gb', 20, 59.99], ['50gb', 50, 99.99]]],
];

const travel = travelTiers.flatMap(([periodSlug, duration, tiers]) => tiers.map(([dataSlug, data, amount]) => ({
  slug: `travel-esim-${dataSlug}-${periodSlug}`,
  name: `${data}GB Travel eSIM — ${duration}`,
  price: `£${amount.toFixed(2)}`,
  amount,
  duration,
  data: `${data}GB`,
  description: `${data}GB of high-speed travel data delivered by eSIM for ${duration}, with no subscription.`,
  category: 'travel', categoryLabel: 'Travel eSIM Packs', accent: '#2dd4bf', eyebrow: 'TRAVEL DATA',
  paymentMethods: ['Stripe', 'PayPal'], countries: supportedTravelCountries,
  features: [`${data}GB high-speed mobile data`, 'eSIM included', `Valid for ${duration}`, 'Optional UK or USA virtual number', 'No subscription or auto-renewal'],
})));

const privacyTiers = [
  ['1-week', '7 days', [['1gb', 1, 10.99], ['5gb', 5, 17.99], ['10gb', 10, 24.99], ['50gb', 50, 49.99]]],
  ['2-week', '14 days', [['5gb', 5, 29.99], ['10gb', 10, 41.99], ['50gb', 50, 84.99]]],
  ['30-day', '30 days', [['10gb', 10, 42.99], ['20gb', 20, 72.99], ['50gb', 50, 129.99]]],
];

const privacy = privacyTiers.flatMap(([periodSlug, duration, tiers]) => tiers.map(([dataSlug, data, amount]) => ({
  slug: `privacy-pack-${dataSlug}-${periodSlug}`,
  name: `${data}GB Privacy Pack — ${duration}`,
  price: `£${amount.toFixed(2)}`,
  amount,
  duration,
  data: `${data}GB`,
  description: `${data}GB of travel eSIM data bundled with PhantomPathVPN access for ${duration}.`,
  category: 'privacy', categoryLabel: 'Privacy Packs', accent: '#818cf8', eyebrow: 'DATA + VPN',
  paymentMethods: ['Stripe', 'PayPal'], countries: supportedTravelCountries,
  features: [`${data}GB high-speed eSIM data`, 'PhantomPathVPN access included', `Valid for ${duration}`, 'Optional UK or USA virtual number', 'One-off payment'],
})));

const unlimited = [
  {
    slug: 'unlimited-data-30-day', name: 'Unlimited Data — 30 Days', price: '£129.99', amount: 129.99,
    duration: '30 days', description: 'Unlimited high-speed UK travel data for heavy travellers and power users.',
    category: 'unlimited', categoryLabel: 'Unlimited Packs', accent: '#fde047', eyebrow: 'UNLIMITED',
    paymentMethods: ['Stripe', 'PayPal'], countries: supportedTravelCountries,
    features: ['Unlimited high-speed data', 'eSIM included', '30-day duration', 'Optional UK or USA virtual number', 'No subscription'],
  },
  {
    slug: 'unlimited-data-vpn-30-day', name: 'Unlimited Data + VPN — 30 Days', price: '£139.99', amount: 139.99,
    duration: '30 days', description: 'Unlimited high-speed UK data plus premium PhantomPathVPN access.',
    category: 'unlimited', categoryLabel: 'Unlimited Packs', accent: '#fde047', eyebrow: 'ULTIMATE PRIVACY',
    paymentMethods: ['Stripe', 'PayPal'], countries: supportedTravelCountries,
    features: ['Unlimited high-speed data', 'Premium VPN access included', 'eSIM included', '30-day duration', 'Optional UK or USA virtual number'],
  },
];

const numbers = [
  {
    slug: 'uk-virtual-number-30-day', name: 'UK Virtual Number — 30 Days', price: '£4.99', amount: 4.99,
    duration: '30 days', description: 'A dedicated UK virtual number that works alongside your existing SIM or eSIM.',
    category: 'numbers', categoryLabel: 'Virtual Numbers', accent: '#38bdf8', eyebrow: 'STAY REACHABLE', flag: '🇬🇧',
    paymentMethods: ['Stripe', 'PayPal'],
    features: ['Dedicated UK virtual number', 'Works with an existing SIM or eSIM', 'Optional minutes and SMS', 'No contract', 'Suitable for general communication where supported'],
  },
  {
    slug: 'usa-virtual-number-30-day', name: 'USA Virtual Number — 30 Days', price: '£4.99', amount: 4.99,
    duration: '30 days', description: 'A dedicated USA virtual number that works alongside your existing SIM or eSIM.',
    category: 'numbers', categoryLabel: 'Virtual Numbers', accent: '#38bdf8', eyebrow: 'STAY REACHABLE', flag: '🇺🇸',
    paymentMethods: ['Stripe', 'PayPal'], requiresUsaConsent: true,
    features: ['Dedicated USA virtual number', 'Works with an existing SIM or eSIM', 'Optional minutes and SMS', 'No contract', 'Transactional SMS consent required'],
  },
];

export const products = [...vpn, ...travel, ...privacy, ...unlimited, ...numbers];

export const productCategories = [
  { id: 'vpn', label: 'VPN Passes', description: 'Private access from 20p', accent: '#00ffc8' },
  { id: 'travel', label: 'Travel eSIM Packs', description: 'High-speed data abroad', accent: '#2dd4bf' },
  { id: 'privacy', label: 'Privacy Packs', description: 'eSIM data bundled with VPN', accent: '#818cf8' },
  { id: 'unlimited', label: 'Unlimited Packs', description: '30-day unlimited data', accent: '#fde047' },
  { id: 'numbers', label: 'Virtual Numbers', description: 'UK and USA numbers', accent: '#38bdf8' },
];

export const getProductBySlug = (slug) => products.find((product) => product.slug === slug);
export const getProductsByCategory = (category) => products.filter((product) => product.category === category);
